// client/src/components/pages/LexiStar/Drivers/AllocationEditor.jsx
//
// Allocate a driver to an asset for a span (FR-DRV-002/003): driver (when not
// fixed), asset, from → to on the shared date-time range picker, or open-ended.
// The database refuses two drivers on one asset at once (EXCLUDE constraint);
// the 409 is shown with the allocation it clashes with, in plain words.

import React, { useEffect, useState } from 'react';
import ThemedSelect from '../../../common/ThemedSelect';
import UnifiedDateTimeRangePicker from '../../../common/UnifiedDateTimeRangePicker';
import { inputClasses } from '../StatusBoard/atoms';
import { fmtDateTime } from '../StatusBoard/format';
import { createAllocation } from './driversApi';
import { errorText } from './DriverDialog';

const pad = (n) => String(n).padStart(2, '0');
const localValue = (d) =>
  d
    ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    : '';
const toIso = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export function conflictText(conflicts = []) {
  return conflicts
    .map(
      (c) =>
        `${c.registration || 'This truck'} is allocated to ${c.driver_name || 'another driver'} from ${fmtDateTime(c.from)}${c.to ? ` to ${fmtDateTime(c.to)}` : ' (open-ended)'}`
    )
    .join('; ');
}

export default function AllocationEditor({
  driverId = null,
  assetId = null,
  defaultFrom = null,
  drivers = [],
  assets = [],
  onSaved,
  onCancel,
}) {
  const [driver, setDriver] = useState(driverId || '');
  const [asset, setAsset] = useState(assetId || '');
  const [from, setFrom] = useState(() => localValue(defaultFrom || new Date()));
  const [to, setTo] = useState('');
  const [openEnded, setOpenEnded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDriver(driverId || '');
  }, [driverId]);
  useEffect(() => {
    setAsset(assetId || '');
  }, [assetId]);

  const fromIso = toIso(from);
  const toIsoValue = openEnded ? null : toIso(to);
  const badRange = !openEnded && (!toIsoValue || (fromIso && toIsoValue <= fromIso));
  const ready = driver && asset && fromIso && !badRange && !saving;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await createAllocation({
        driver_id: driver,
        asset_id: asset,
        from: fromIso,
        ...(toIsoValue ? { to: toIsoValue } : {}),
      });
      onSaved?.(res?.allocation || res);
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 409 && Array.isArray(data.conflicts) && data.conflicts.length) {
        setError(`${data.error || 'Overlapping allocation'} — ${conflictText(data.conflicts)}.`);
      } else {
        setError(errorText(err, 'The allocation was not saved'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
      aria-label="Allocation editor"
      role="group"
    >
      {error && (
        <p
          role="alert"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {error}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {!driverId && (
          <div>
            <label htmlFor="alloc-driver" className="block text-xs font-medium text-slate-600 mb-1">
              Driver
            </label>
            <ThemedSelect
              id="alloc-driver"
              value={driver}
              onChange={(e) => setDriver(e.target.value)}
              className={inputClasses}
            >
              <option value="">Choose a driver...</option>
              {drivers
                .filter((d) => d.active !== false)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {d.tag ? ` · ${d.tag}` : ''}
                  </option>
                ))}
            </ThemedSelect>
          </div>
        )}
        {!assetId && (
          <div>
            <label htmlFor="alloc-asset" className="block text-xs font-medium text-slate-600 mb-1">
              Truck
            </label>
            <ThemedSelect
              id="alloc-asset"
              value={asset}
              onChange={(e) => setAsset(e.target.value)}
              className={inputClasses}
            >
              <option value="">Choose a truck...</option>
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.registration}
                  {a.fleet_no ? ` · ${a.fleet_no}` : ''}
                </option>
              ))}
            </ThemedSelect>
          </div>
        )}
        <div className="sm:col-span-2">
          <span className="block text-xs font-medium text-slate-600 mb-1">
            {openEnded ? 'From' : 'From → to'}
          </span>
          <UnifiedDateTimeRangePicker
            startValue={from}
            endValue={openEnded ? '' : to}
            onStartChange={setFrom}
            onEndChange={(v) => {
              setTo(v);
              if (v) setOpenEnded(false);
            }}
            placeholder="Choose when"
          />
          <label className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={openEnded}
              onChange={(e) => setOpenEnded(e.target.checked)}
              className="rounded border-slate-300"
            />
            Open-ended (until changed)
          </label>
          {badRange && (
            <p className="text-xs text-red-700 mt-1">
              Set an end after the start, or make it open-ended.
            </p>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={!ready}
          className="px-4 py-1.5 text-sm font-semibold text-white rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Allocate'}
        </button>
      </div>
    </div>
  );
}
